// Deterministic token derivation for Trail devices.
//
// SINGLE SOURCE OF TRUTH, imported by both the server-side issuer
// (`functions/api/v1/devices.ts`) and the frontend offline fallback
// (`src/main.ts`). Both must produce byte-identical output for identical
// input — keeping one copy is what guarantees that.
//
// Identity is opaque (ADR 0011): the label/group segments are arbitrary
// keys, not a fixed word list. `SHORT` is a *display* abbreviation for
// the 6 animal keys so the SPA keeps its `trail-bea-fox-owl-…` vanity
// token byte-for-byte; any segment not in `SHORT` falls through to its
// raw form, so commercial labels still produce a sensible token.

export const SHORT: Record<string, string> = {
  bear:   'bea',
  fox:    'fox',
  owl:    'owl',
  rabbit: 'har',
  mouse:  'mou',
  frog:   'frg',
};

const ALPHA = 'abcdefghjkmnpqrstuvwxyz23456789';

export function tokenFor(animals: string[], group: string[] | null = null, attempt = 0): string {
  const slug = animals.map((k) => SHORT[k] ?? k).join('-');
  const gOk = !!group && group.length === 2 && group.every(Boolean);
  const gSlug = gOk ? group!.map((k) => SHORT[k] ?? k).join('-') + '-' : '';
  const attemptSalt = attempt > 0 ? '/' + attempt : '';
  const seed = gSlug + slug + animals.join('|') + (group || []).join('|') + attemptSalt;
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) % 0xfffff;
  let tail = '';
  for (let i = 0; i < 4; i++) {
    tail += ALPHA[h % ALPHA.length];
    h = Math.floor(h / ALPHA.length) ^ (h << 5);
    h = Math.abs(h) % 0xfffff;
  }
  return `trail-${gSlug}${slug}-${tail}`;
}

// Opaque key segments: lowercase alnum + underscore, 1–32 chars. The
// hyphen is reserved as the group-path separator, so it is excluded from
// the segment charset. Identity is opaque (ADR 0011) — this is a generic
// format check, not an animal word list. The 6 animal keys and every
// group pair the SPA sends satisfy it trivially.
const KEY_SEGMENT_RE = /^[a-z0-9_]{1,32}$/;

export function isValidKeySegment(value: unknown): value is string {
  return typeof value === 'string' && KEY_SEGMENT_RE.test(value);
}

// A device label: 1–8 opaque key segments. The animal SPA always sends
// exactly 3; commercial callers may send any 1–8.
export function isValidLabel(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= 8 &&
    value.every(isValidKeySegment)
  );
}

// A group/cohort key: empty/absent (ungrouped) or exactly 2 opaque key
// segments. Two segments is load-bearing — they serialise to the
// `g1-g2` cohort path and the canonical at-rest column form (group.ts).
export function isValidGroupKey(value: unknown): value is string[] | null | undefined {
  if (value == null) return true;
  if (!Array.isArray(value)) return false;
  if (value.length === 0) return true;
  return value.length === 2 && value.every(isValidKeySegment);
}
